import { useMemo } from 'react'

import {
  useModals, useNumericParams, useSetState, useShowModal,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditApiKeyModal from '@modals/CreateOrEditApiKeyModal'
import CreateOrEditCustomShortcodeModal from '@modals/CreateOrEditCustomShortcodeModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import GenerateAuthTokenModal from '@modals/GenerateAuthTokenModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import Manage2FASettingsModal from '@modals/Manage2FASettingsModal'
import ManageEntitySupportTeamModal from '@modals/ManageEntitySupportTeamModal'
import ManageUserApplicationsModal from '@modals/ManageUserApplicationsModal'
import ManageUserTeamMemberRolesModal from '@modals/ManageUserTeamMemberRolesModal'

import { ActivityModel, ImageModel, UserModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditApiKeyModal: false,
  showCreateOrEditCustomShortcodeModal: false,
  showCreateOrEditImageModal: false,
  showGenerateAuthTokenModal: false,
  showManageActivityModal: false,
  showManage2FASettingsModal: false,
  showManageEntitySupportTeamModal: false,
  showManageUserApplicationsModal: false,
  showManageUserTeamMemberRolesModal: false,
}

const callbacks = (
  component: keyof typeof componentCallbacks,
  setState: (payload: Partial<typeof defaultState>) => void,
) => {
  const componentCallbacks = {
    CreateOrEditApiKeyModal: {
      closeModal: () => setState({ showCreateOrEditApiKeyModal: false }),
      createApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditCustomShortcodeModal: {
      closeModal: () => setState({ showCreateOrEditCustomShortcodeModal: false }),
      createCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCustomShortcode: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    GenerateAuthTokenModal: {
      closeModal: () => setState({ showGenerateAuthTokenModal: false }),
      generateAuthToken: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    Manage2FASettingsModal: {
      closeModal: () => setState({ showManage2FASettingsModal: false }),
      toggleDisable2FA: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTwoFactorMethod: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageEntitySupportTeamModal: {
      closeModal: () => setState({ showManageEntitySupportTeamModal: false }),
      updateEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageUserApplicationsModal: {
      closeModal: () => setState({ showManageUserApplicationsModal: false }),
      updateUserApplications: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageUserTeamMemberRolesModal: {
      closeModal: () => setState({ showManageUserTeamMemberRolesModal: false }),
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditApiKeyModal,
    showCreateOrEditCustomShortcodeModal,
    showCreateOrEditImageModal,
    showGenerateAuthTokenModal,
    showManageActivityModal,
    showManage2FASettingsModal,
    showManageEntitySupportTeamModal,
    showManageUserApplicationsModal,
    showManageUserTeamMemberRolesModal,
  } = state

  const { userId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      exportUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrEditApiKeyModal: (payload: object) => {
        setModalData('CreateOrEditApiKeyModal', payload)
        setState({ showCreateOrEditApiKeyModal: true })
      },
      showCreateOrEditCustomShortcodeModal: (payload: object) => {
        setModalData('CreateOrEditCustomShortcodeModal', payload)
        setState({ showCreateOrEditCustomShortcodeModal: true })
      },
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showGenerateAuthTokenModal: (payload: { entity: UserModel }) => {
        setModalData('GenerateAuthTokenModal', payload)
        setState({ showGenerateAuthTokenModal: true })
      },
      showManageActivityModal: (payload: ActivityModel) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManage2FASettingsModal: (payload: { entity: UserModel }) => {
        setModalData('Manage2FASettingsModal', payload)
        setState({ showManage2FASettingsModal: true })
      },
      showManageEntitySupportTeamModal: (payload: { entity: UserModel }) => {
        setModalData('ManageEntitySupportTeamModal', payload)
        setState({ showManageEntitySupportTeamModal: true })
      },
      showManageUserApplicationsModal: (payload: { entity: UserModel }) => {
        setModalData('ManageUserApplicationsModal', payload)
        setState({ showManageUserApplicationsModal: true })
      },
      showManageUserTeamMemberRolesModal: (payload: { entity: UserModel }) => {
        setModalData('ManageUserTeamMemberRolesModal', payload)
        setState({ showManageUserTeamMemberRolesModal: true })
      },
      updateUser: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  useShowModal({
    modalKey: 'ManageUserTeamMemberRolesModal',
    options: {
      callbacks: pageContext.callbacks,
    },
  })

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent userId={userId} />

        <CreateOrEditApiKeyModal
          callbacks={callbacks('CreateOrEditApiKeyModal', setState)}
          showModal={showCreateOrEditApiKeyModal}
        />

        <CreateOrEditCustomShortcodeModal
          callbacks={callbacks('CreateOrEditCustomShortcodeModal', setState)}
          showModal={showCreateOrEditCustomShortcodeModal}
        />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <GenerateAuthTokenModal
          callbacks={callbacks('GenerateAuthTokenModal', setState)}
          showModal={showGenerateAuthTokenModal}
        />

        <Manage2FASettingsModal
          callbacks={callbacks('Manage2FASettingsModal', setState)}
          showModal={showManage2FASettingsModal}
        />

        <ManageEntitySupportTeamModal
          callbacks={callbacks('ManageEntitySupportTeamModal', setState)}
          showModal={showManageEntitySupportTeamModal}
        />

        <ManageUserApplicationsModal
          callbacks={callbacks('ManageUserApplicationsModal', setState)}
          showModal={showManageUserApplicationsModal}
        />

        <ManageUserTeamMemberRolesModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageUserTeamMemberRolesModal', setState)}
          showModal={showManageUserTeamMemberRolesModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
